import { PlusCircleOutlined } from "@ant-design/icons"
import { DOCUMENT_SUPPORTED_FORMAT_MAP } from "@finway-group/shared/lib/consts/common"
import { DocumentTypeEnum } from "@finway-group/shared/lib/models/common/documentType.enum"
import { type DocumentCoreData } from "@finway-group/shared/lib/models/file/documentCoreData"
import { Button, Spin, Switch, Tooltip } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FileViewer } from "~Components/fileViewer/FileViewer"
import { UploadFilesDragger } from "~Components/upload/UploadFilesDragger"
import { AXIOS_CANCELLED_MESSAGE } from "~Shared/config/consts"
import { FileService, OcrService, UserService } from "~Shared/services"
import { type VendorReadFileAiResponse } from "~Shared/services/ocr.service"
import { mapDocumentTypeToUploadKind } from "~Shared/utils/expense.utils"
import { JsonViewer } from "../../../../components/JsonViewer"
import { AIIcon } from "../icons/AIIcon"
import { CloseIcon } from "../icons/CloseIcon"
import { FileFilledIcon } from "../icons/FileFilledIcon"

interface FileZoneProps {
    onChange?: (data: DocumentCoreData) => void
    onOcrChange?: <DataType extends VendorReadFileAiResponse>(data: DataType) => void
    initialFile?: DocumentCoreData
    onRemove?: () => void
    withToolbar?: boolean
    disabled?: boolean
    documentType?: DocumentTypeEnum
    ocrDisabled?: boolean
}

export const FileZone = ({
    onChange,
    initialFile,
    onRemove,
    onOcrChange,
    withToolbar = true,
    disabled = false,
    documentType = DocumentTypeEnum.PURCHASE_ORDER,
    ocrDisabled = false,
}: FileZoneProps) => {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)

    const loggedInUser = UserService.getLoggedInEmployeeProfile()

    // OCR state
    const ocrAbortController = useRef(new AbortController())
    const [isOcrEnabled, setIsOcrEnabled] = useState(loggedInUser.settings.enableOcr)
    const [isOcrUploading, setIsOcrUploading] = useState(false)
    const [ocrData, setOcrData] = useState<object | null>(null)

    const onUnmount = useCallback(() => {
        ocrAbortController.current.abort(AXIOS_CANCELLED_MESSAGE)
    }, [ocrAbortController])

    useEffect(
        () => () => {
            onUnmount()
        },
        [],
    )

    const handleFileUpload = useCallback(
        async (files: Array<File>) => {
            if (!files || files.length === 0) {
                return
            }

            if (!FileService.checkFilesSizeAndHandleError(Array.from(files))) {
                return
            }

            if (!FileService.checkIsFileExtensionValidAndShowNotification(files[0], DOCUMENT_SUPPORTED_FORMAT_MAP[DocumentTypeEnum.PURCHASE_ORDER])) {
                return
            }

            // Only take the first file
            const file = files[0]

            const data = await FileService.upload(file, mapDocumentTypeToUploadKind(documentType))
            if (!data.fileId) {
                return
            }

            handleOcrUpload({
                file: { fileId: data.fileId, url: data.url, name: data.originalname },
                isOcrSettingEnabled: isOcrEnabled,
            })
            onChange?.({ fileId: data.fileId, url: data.url, name: data.originalname })
        },
        [onChange, isOcrEnabled],
    )

    const handleIconClick = () => {
        inputRef.current?.click()
    }

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault()
    }

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault()
    }

    const handleDrop = async (event: React.DragEvent) => {
        event.preventDefault()

        await handleFileUpload(Array.from(event.dataTransfer.files))
    }

    const handleRemoveFile = () => {
        // Clear the file input
        if (inputRef.current) {
            inputRef.current.value = ""
        }

        onRemove?.()
    }

    // OCR toggle handlers
    const handleOcrToggle = (checked: boolean) => {
        setIsOcrEnabled(checked)
        handleOcrUpload({
            file: initialFile,
            isOcrSettingEnabled: checked,
        })
    }

    const handleOcrUpload = ({ isOcrSettingEnabled, file }: { file?: DocumentCoreData; isOcrSettingEnabled: boolean }) => {
        if (!isOcrSettingEnabled || !file?.fileId) {
            return
        }

        void OcrService.runOcrAi({
            fileId: file.fileId,
            documentType,
            setIsOcrUploading,
            abortSignal: ocrAbortController.current.signal,
        }).then((data) => {
            if (data) {
                setOcrData(data)
                onOcrChange?.(data)
            }
        })
    }

    // OCR toggles component
    const ocrToggles = (
        <div className="mr-10 ml-10 mb-10 text-sm flex items-center gap-10 justify-end">
            <JsonViewer json={ocrData} />
            {isOcrUploading && <Spin className="mr-10" />}

            <Tooltip placement="bottom" title={t("tooltips:ai_ocr_hint")} color="#9747FF">
                <>
                    <AIIcon />
                </>
            </Tooltip>

            <Tooltip placement="topLeft" title={t("tooltips:ocr_hint")}>
                <span>{t("input:settings.enable_ocr")}</span>
            </Tooltip>

            <Tooltip title={ocrDisabled ? t("tooltips:ocr_disabled_hint") : undefined} placement="top">
                <Switch data-testid="enableOcrToggle" size="small" checked={isOcrEnabled} onChange={handleOcrToggle} disabled={isOcrUploading || ocrDisabled} />
            </Tooltip>
        </div>
    )

    return (
        <div className="w-full">
            {!initialFile?.url || !initialFile?.name ? (
                <div>
                    {withToolbar && ocrToggles}
                    <UploadFilesDragger doFileUpload={handleFileUpload} documentType={documentType} disabled={disabled}>
                        <i className="el-icon-upload text-lg" />
                        <div className="el-upload__text">
                            <Button
                                data-testid="addInvoiceInbox"
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={handleIconClick}
                                style={{ cursor: "pointer" }}
                                disabled={disabled}
                            >
                                <PlusCircleOutlined />
                                {documentType === DocumentTypeEnum.PURCHASE_ORDER && t("action:add_purchase_order")}
                                {documentType === DocumentTypeEnum.DELIVERY_NOTE && t("action:add_delivery_note")}
                                {documentType === DocumentTypeEnum.INVOICE && t("action:add_invoice")}
                            </Button>
                            <br />
                            <small>{t("action:or_drop_files")}</small>
                        </div>
                    </UploadFilesDragger>
                </div>
            ) : (
                <div className="w-full">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-6 rounded-sm px-10 py-5 mb-10 " style={{ backgroundColor: "#02C2A60D" }}>
                            <FileFilledIcon />{" "}
                            <span className="font-medium overflow-hidden text-ellipsis whitespace-nowrap" style={{ width: "200px" }}>
                                {initialFile.name}
                            </span>
                            {!disabled ? <Button type="text" size="middle" icon={<CloseIcon />} onClick={handleRemoveFile} className="bg-transparent" disabled={disabled} /> : null}
                        </div>
                        <div className="flex  gap-2 justify-center items-center">{ocrToggles}</div>
                    </div>
                    <div className="w-full">
                        <FileViewer
                            filePath={initialFile.url || ""}
                            showPaidStamp={false}
                            pdfHeight="auto"
                            onUnsupportedFormatDownload={() => {
                                const link = document.createElement("a")
                                link.href = initialFile.url || ""
                                link.download = initialFile.name
                                link.click()
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
